import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { MapPin, ArrowRight, Clock } from "lucide-react";

const FALLBACK_HERO =
  "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&q=80&w=1600";

/**
 * Homepage "Popular Destinations" section. Renders up to 3 featured category
 * pages picked by the admin (`featured_order` on each category doc). Each
 * card links to the category landing page. The layout mirrors the mock:
 *  - 1 large hero card on the left (position 1)
 *  - 2 smaller stacked cards on the right (positions 2 & 3)
 * When less than 3 categories are featured, the grid collapses gracefully.
 */
export default function PopularDestinations() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/popular-destinations`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (items.length === 0) return null;

  const [big, ...rest] = items;

  return (
    <section
      className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
      data-testid="popular-destinations-section"
    >
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-serif text-white mb-3">Popular Destinations</h2>
        <p className="text-white/50 max-w-2xl mx-auto">
          Fixed-rate transfers from Venezia to Italy&apos;s most sought-after locations
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {big && <BigCard cat={big} />}
        {rest.length > 0 && (
          <div className="flex flex-col gap-6">
            {rest.map((c) => (
              <SmallCard key={c.slug} cat={c} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CardCategory({ cat }) {
  return (
    <div
      className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-1"
      data-testid={`popular-cat-label-${cat.slug}`}
    >
      {cat.keywords?.split(",")[0]?.trim() || cat.name}
    </div>
  );
}

function BigCard({ cat }) {
  return (
    <Link
      to={`/routes/category/${cat.slug}`}
      className="group relative rounded-sm overflow-hidden border border-white/5 hover:border-[#C5A059]/40 transition-all min-h-[420px] flex items-end"
      style={{
        backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.05) 100%), url(${cat.hero_image_url || FALLBACK_HERO})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid={`popular-destination-${cat.slug}`}
    >
      <div className="p-6 sm:p-8 w-full">
        <CardCategory cat={cat} />
        <h3 className="text-3xl sm:text-4xl font-serif text-white leading-tight drop-shadow mb-3">
          {cat.name}
        </h3>
        {cat.sample_destinations?.length > 0 && (
          <p className="text-white/80 text-sm mb-4">
            {cat.sample_destinations.slice(0, 4).join(" · ")}
          </p>
        )}
        <div className="flex items-center gap-4 text-sm text-white/80">
          {cat.min_base_price != null && (
            <span>
              From <span className="text-[#C5A059] font-semibold">€{Math.round(cat.min_base_price)}</span>
            </span>
          )}
          {cat.duration_range && (
            <span className="flex items-center gap-1 text-white/60">
              <Clock className="w-3 h-3" /> {cat.duration_range} from Venezia
            </span>
          )}
          <ArrowRight className="w-4 h-4 text-[#C5A059] ml-auto group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function SmallCard({ cat }) {
  return (
    <Link
      to={`/routes/category/${cat.slug}`}
      className="group relative rounded-sm overflow-hidden border border-white/5 hover:border-[#C5A059]/40 transition-all min-h-[195px] flex items-end"
      style={{
        backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 65%, rgba(0,0,0,0.05) 100%), url(${cat.hero_image_url || FALLBACK_HERO})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid={`popular-destination-${cat.slug}`}
    >
      <div className="p-5 sm:p-6 w-full">
        <CardCategory cat={cat} />
        <h3 className="text-xl sm:text-2xl font-serif text-white leading-tight drop-shadow mb-1">
          {cat.name}
        </h3>
        {cat.sample_destinations?.length > 0 && (
          <p className="text-white/70 text-xs mb-2">
            {cat.sample_destinations.slice(0, 3).join(" · ")}
          </p>
        )}
        {cat.min_base_price != null && (
          <div className="text-white/70 text-sm flex items-center gap-2">
            <MapPin className="w-3 h-3 text-[#C5A059]" />
            From <span className="text-[#C5A059] font-semibold">€{Math.round(cat.min_base_price)}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
